import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';

interface ProblemResult {
  problemNumber: number;
  problemStatement: string;
  answerChoices: Record<string, string>;
  correctAnswer: string;
  userAnswer: string | null;
  isCorrect: boolean;
  timeSpent: number | null;
  difficulty: string;
  mainTopic: string;
  subtopic?: string;
  relatedConcepts: string[];
  aopsSourceLink?: string;
  problemImage?: string;
  attemptDate: string | null;
}

interface ContestInfo {
  name: string;
  year: number;
  contestType: string;
  version: string;
}

interface TestSummary {
  contest: ContestInfo;
  overall: {
    totalQuestions: number;
    answeredQuestions: number;
    correctAnswers: number;
    accuracy: number;
    totalTimeSeconds: number;
    averageTimePerQuestion: number;
  };
  byDifficulty: {
    easy: { total: number; correct: number; accuracy: number; };
    medium: { total: number; correct: number; accuracy: number; };
    hard: { total: number; correct: number; accuracy: number; };
  };
}

interface TestResults {
  results: ProblemResult[];
  summary: TestSummary;
}

interface AMCTestResultsProps {
  contestId: string;
  onBack: () => void;
  onRetakeTest: () => void;
}

const AMCTestResults: React.FC<AMCTestResultsProps> = ({ contestId, onBack, onRetakeTest }) => {
  const { token } = useUser();
  const [testResults, setTestResults] = useState<TestResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProblem, setSelectedProblem] = useState<number | null>(null);

  useEffect(() => {
    if (token) {
      fetchTestResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId, token]);

  const fetchTestResults = async () => {
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await apiService.get(`/amc/test/results/${contestId}`);
      setTestResults(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  const formatTimeShort = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTopicBreakdown = () => {
    if (!testResults) return [];
    
    const topicStats = testResults.results.reduce((acc, result) => {
      const topic = result.mainTopic;
      if (!acc[topic]) {
        acc[topic] = { total: 0, correct: 0, attempted: 0 };
      }
      acc[topic].total++;
      if (result.userAnswer) acc[topic].attempted++;
      if (result.isCorrect) acc[topic].correct++;
      return acc;
    }, {} as Record<string, { total: number; correct: number; attempted: number; }>);

    return Object.entries(topicStats).map(([topic, stats]) => ({
      topic,
      ...stats,
      accuracy: stats.attempted > 0 ? (stats.correct / stats.attempted) * 100 : 0
    }));
  };

  const calculateAMCScore = () => {
    if (!testResults) return 0;
    const { results } = testResults;
    
    let score = 0;
    results.forEach(result => {
      if (result.isCorrect) {
        score += 6; // 6 points for correct answer
      } else if (result.userAnswer === null || result.userAnswer === '') {
        score += 1.5; // 1.5 points for blank
      }
      // 0 points for incorrect answer
    });
    
    return Math.round(score * 10) / 10; // Round to 1 decimal place
  };

  const isAIMEQualified = () => {
    const score = calculateAMCScore();
    return score >= 100; // Typical AIME qualification threshold
  };

  const getRecommendations = () => {
    const topics = getTopicBreakdown();
    return topics
      .filter(topic => topic.attempted > 0 && topic.accuracy < 70)
      .sort((a, b) => a.accuracy - b.accuracy)
      .slice(0, 3)
      .map(topic => ({
        topic: topic.topic,
        accuracy: topic.accuracy,
        suggestion: getTopicSuggestion(topic.topic)
      }));
  };

  const getTopicSuggestion = (topic: string): string => {
    const suggestions: Record<string, string> = {
      'Algebra': 'Focus on polynomial manipulation, systems of equations, and function analysis',
      'Geometry': 'Review similarity, congruency, and coordinate geometry principles',
      'Number Theory': 'Practice divisibility rules, modular arithmetic, and prime factorization',
      'Counting & Probability': 'Study combinatorics principles and probability theory',
      'Logic': 'Work on logical reasoning and problem-solving strategies'
    };
    return suggestions[topic] || 'Practice more problems in this area';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            borderTopColor: 'white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: 'white', fontSize: '18px' }}>Loading results...</p>
        </div>
        <style>
          {`@keyframes spin { to { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '40px',
          borderRadius: '16px',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ color: '#ef4444', marginBottom: '20px', fontSize: '18px' }}>{error}</p>
          <button onClick={fetchTestResults} style={{
            background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '16px'
          }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!testResults) {
    return <div>No results found</div>;
  }

  const { results, summary } = testResults;
  const topicBreakdown = getTopicBreakdown();
  const amcScore = calculateAMCScore();
  const isQualified = isAIMEQualified();
  const recommendations = getRecommendations();
  const leftBlank = results.filter(r => !r.userAnswer).length;
  const incorrectAnswers = results.filter(r => r.userAnswer && !r.isCorrect).length;

  // Calculate difficulty stats
  const easyStats = {
    total: results.filter(r => r.problemNumber <= 8).length,
    correct: results.filter(r => r.problemNumber <= 8 && r.isCorrect).length
  };
  const mediumStats = {
    total: results.filter(r => r.problemNumber >= 9 && r.problemNumber <= 16).length,
    correct: results.filter(r => r.problemNumber >= 9 && r.problemNumber <= 16 && r.isCorrect).length
  };
  const hardStats = {
    total: results.filter(r => r.problemNumber >= 17).length,
    correct: results.filter(r => r.problemNumber >= 17 && r.isCorrect).length
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        {/* Header */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <button onClick={onBack} style={{
            position: 'absolute',
            left: '32px',
            top: '32px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '8px 16px',
            textDecoration: 'none',
            color: '#64748b',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}>
            <ChevronLeft size={16} />
            Back to Tests
          </button>
          
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>{summary.contest.name} Test Results</h1>
          <p style={{ color: '#64748b', fontSize: '16px' }}>
            Completed • Duration: {formatTime(summary.overall.totalTimeSeconds)}
          </p>
        </div>

        {/* Overview Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px', color: '#10b981' }}>
              {summary.overall.correctAnswers}/25
            </div>
            <div style={{ color: '#64748b', fontSize: '14px', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px' }}>
              Answered Correctly
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px', color: '#f59e0b' }}>
              {leftBlank}
            </div>
            <div style={{ color: '#64748b', fontSize: '14px', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px' }}>
              Left Blank
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px', color: '#333' }}>
              {amcScore}
            </div>
            <div style={{ color: '#64748b', fontSize: '14px', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px' }}>
              Total Score
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '36px', fontWeight: '700', marginBottom: '8px', color: '#f59e0b' }}>
              {Math.floor(summary.overall.totalTimeSeconds / 60)}m
            </div>
            <div style={{ color: '#64748b', fontSize: '14px', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px' }}>
              Time Taken
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {/* Question Results */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🏆 Question Results
            </h2>
            
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '16px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: '#64748b'
              }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#10b981' }}></div>
                Correct
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: '#64748b'
              }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#ef4444' }}></div>
                Incorrect
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '12px',
                color: '#64748b'
              }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#f8fafc', border: '2px solid #cbd5e1' }}></div>
                Left Blank
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(45px, 1fr))',
              gap: '8px',
              marginBottom: '24px'
            }}>
              {results.map(result => (
                <div
                  key={result.problemNumber}
                  onClick={() => setSelectedProblem(result.problemNumber)}
                  style={{
                    width: '45px',
                    height: '45px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    background: result.isCorrect ? '#10b981' : 
                               result.userAnswer ? '#ef4444' : '#f8fafc',
                    color: result.isCorrect ? 'white' : 
                           result.userAnswer ? 'white' : '#64748b',
                    border: !result.userAnswer ? '2px solid #cbd5e1' : 'none',
                    ...(result.timeSpent && result.timeSpent > 240 ? { border: '2px solid #f59e0b' } : {}),
                    ...(selectedProblem === result.problemNumber ? { ring: '2px solid #3F8EF6', ringOffset: '2px' } : {})
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  title={`Question ${result.problemNumber}: ${result.isCorrect ? 'Correct' : result.userAnswer ? 'Incorrect' : 'Not Answered'}`}
                >
                  {result.problemNumber}
                </div>
              ))}
            </div>
          </div>

          {/* AIME Readiness */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ⚡ AIME Readiness
            </h2>
            
            <div style={{
              padding: '16px',
              borderRadius: '12px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: isQualified ? 'linear-gradient(135deg, #8b5cf6, #A0F0D0)' : 'linear-gradient(135deg, #f59e0b, #fbbf24)',
              color: 'white'
            }}>
              <div style={{ width: '24px', height: '24px' }}>
                {isQualified ? '✓' : '⚠'}
              </div>
              <div>
                <div style={{ fontWeight: '600' }}>
                  {isQualified ? 'AIME Qualified!' : 'Keep Practicing!'}
                </div>
                <div style={{ fontSize: '14px', opacity: '0.9' }}>
                  Score: {amcScore} (Need: 100+)
                </div>
                <div style={{ fontSize: '12px', opacity: '0.8', marginTop: '4px' }}>
                  {summary.overall.correctAnswers} correct ({summary.overall.correctAnswers * 6} pts) + {leftBlank} blank ({leftBlank * 1.5} pts) + {incorrectAnswers} wrong (0 pts)
                </div>
              </div>
            </div>

            {recommendations.length > 0 && (
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '12px', color: '#1e293b' }}>Focus Areas</h3>
                {recommendations.map((rec, index) => (
                  <div key={index} style={{
                    padding: '12px',
                    background: '#f8fafc',
                    borderLeft: '4px solid #3F8EF6',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }}>
                    <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                      {rec.topic}
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748b' }}>
                      {Math.round(rec.accuracy)}% accuracy - {rec.suggestion}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Performance Analysis Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {/* Performance by Topic */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ⚡ Performance by Topic
            </h2>
            
            {topicBreakdown.map(topic => (
              <div key={topic.topic} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: '1px solid #f1f5f9'
              }}>
                <div style={{ fontWeight: '500', color: '#334155' }}>
                  {topic.topic}
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '100px',
                    height: '6px',
                    background: '#f1f5f9',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
                      borderRadius: '3px',
                      width: `${topic.accuracy}%`
                    }}></div>
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#64748b',
                    minWidth: '40px'
                  }}>
                    {topic.correct}/{topic.attempted}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Difficulty Analysis */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📊 Difficulty Analysis
            </h2>
            
            {[
              { label: 'Easy (1-8)', stats: easyStats },
              { label: 'Medium (9-16)', stats: mediumStats },
              { label: 'Hard (17-25)', stats: hardStats }
            ].map(item => (
              <div key={item.label} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: '1px solid #f1f5f9'
              }}>
                <div style={{ fontWeight: '500', color: '#334155' }}>
                  {item.label}
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '100px',
                    height: '6px',
                    background: '#f1f5f9',
                    borderRadius: '3px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
                      borderRadius: '3px',
                      width: `${(item.stats.correct / item.stats.total) * 100}%`
                    }}></div>
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#64748b',
                    minWidth: '40px'
                  }}>
                    {item.stats.correct}/{item.stats.total}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Time Analysis */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🕐 Time Analysis
          </h2>
          
          <div style={{
            display: 'flex',
            gap: '16px',
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: '#64748b'
            }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#10b981' }}></div>
              Fast (&lt;2min)
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: '#64748b'
            }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#64748b' }}></div>
              Normal (2-4min)
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              color: '#64748b'
            }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#ef4444' }}></div>
              Slow (&gt;4min)
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
            gap: '8px'
          }}>
            {results.map(result => {
              const timeSpent = result.timeSpent || 0;
              const timeClass = timeSpent < 120 ? 'fast' : timeSpent <= 240 ? 'normal' : 'slow';
              const colors = {
                fast: '#10b981',
                normal: '#64748b',
                slow: '#ef4444'
              };
              
              return (
                <div key={result.problemNumber} style={{
                  textAlign: 'center',
                  padding: '8px',
                  borderRadius: '6px',
                  background: timeClass === 'slow' ? '#fef2f2' : '#f8fafc',
                  border: timeClass === 'slow' ? '1px solid #fecaca' : '1px solid #e2e8f0'
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#64748b',
                    marginBottom: '4px'
                  }}>Q{result.problemNumber}</div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: result.userAnswer ? colors[timeClass] : '#64748b'
                  }}>
                    {result.userAnswer ? formatTimeShort(timeSpent) : 'Blank'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Problem Review Modal */}
        {selectedProblem && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
            }}>
              {(() => {
                const problem = results.find(r => r.problemNumber === selectedProblem);
                if (!problem) return null;

                return (
                  <>
                    {/* Modal Header */}
                    <div style={{
                      padding: '24px',
                      borderBottom: '1px solid #e2e8f0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      position: 'sticky',
                      top: 0,
                      background: 'white',
                      zIndex: 10,
                      borderTopLeftRadius: '16px',
                      borderTopRightRadius: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <h3 style={{
                          fontSize: '24px',
                          fontWeight: '700',
                          color: '#1e293b',
                          margin: 0
                        }}>Problem {problem.problemNumber}</h3>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {problem.isCorrect ? (
                            <div style={{
                              background: '#10b981',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '14px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span style={{ fontSize: '16px' }}>✓</span>
                              Correct
                            </div>
                          ) : problem.userAnswer ? (
                            <div style={{
                              background: '#ef4444',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '14px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span style={{ fontSize: '16px' }}>✗</span>
                              Incorrect
                            </div>
                          ) : (
                            <div style={{
                              background: '#64748b',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}>
                              Not Answered
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedProblem(null)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '24px',
                          cursor: 'pointer',
                          color: '#64748b',
                          padding: '4px'
                        }}
                      >
                        ×
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div style={{ padding: '24px' }}>
                      {/* Problem Statement and Image */}
                      <div style={{
                        marginBottom: '32px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        borderLeft: '4px solid #3F8EF6',
                        padding: '20px'
                      }}>
                        <div style={{
                          fontSize: '16px',
                          lineHeight: '1.8',
                          color: '#334155',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {problem.problemStatement}
                        </div>
                        
                        {problem.problemImage && (
                          <div style={{
                            textAlign: 'center',
                            marginTop: '20px'
                          }}>
                            <img 
                              src={problem.problemImage} 
                              alt={`Problem ${problem.problemNumber} diagram`}
                              style={{
                                maxWidth: '100%',
                                height: 'auto'
                              }}
                              onError={(e) => {
                                console.error('Failed to load problem image:', e);
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Answer Choices */}
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#1e293b',
                        marginBottom: '16px'
                      }}>Answer Choices:</h4>
                      
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: '12px',
                        marginBottom: '24px'
                      }}>
                        {Object.entries(problem.answerChoices).map(([choice, value]) => {
                          const isCorrect = choice === problem.correctAnswer;
                          const isUserAnswer = choice === problem.userAnswer;
                          
                          return (
                            <div
                              key={choice}
                              style={{
                                padding: '16px',
                                borderRadius: '12px',
                                border: '2px solid',
                                borderColor: isCorrect ? '#10b981' : isUserAnswer && !isCorrect ? '#ef4444' : '#e2e8f0',
                                background: isCorrect ? '#10b98110' : isUserAnswer && !isCorrect ? '#ef444410' : '#ffffff',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                position: 'relative'
                              }}
                            >
                              <div style={{
                                fontWeight: '700',
                                fontSize: '16px',
                                color: isCorrect ? '#10b981' : isUserAnswer && !isCorrect ? '#ef4444' : '#64748b',
                                minWidth: '24px'
                              }}>
                                {choice}.
                              </div>
                              <div style={{
                                flex: 1,
                                fontSize: '16px',
                                color: '#334155'
                              }}>
                                {value}
                              </div>
                              {isCorrect && (
                                <div style={{
                                  position: 'absolute',
                                  right: '16px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  background: '#10b981',
                                  color: 'white',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '700',
                                  fontSize: '14px'
                                }}>
                                  ✓
                                </div>
                              )}
                              {isUserAnswer && !isCorrect && (
                                <div style={{
                                  position: 'absolute',
                                  right: '16px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  background: '#ef4444',
                                  color: 'white',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: '700',
                                  fontSize: '14px'
                                }}>
                                  ✗
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Additional Info */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        marginBottom: '24px',
                        padding: '16px',
                        background: '#f8fafc',
                        borderRadius: '12px'
                      }}>
                        <div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Topic</div>
                          <div style={{ fontWeight: '600', color: '#1e293b' }}>{problem.mainTopic}</div>
                        </div>
                        {problem.subtopic && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Subtopic</div>
                            <div style={{ fontWeight: '600', color: '#1e293b' }}>{problem.subtopic}</div>
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Difficulty</div>
                          <div style={{ fontWeight: '600', color: '#1e293b', textTransform: 'capitalize' }}>{problem.difficulty}</div>
                        </div>
                        {problem.timeSpent && (
                          <div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Time Spent</div>
                            <div style={{ fontWeight: '600', color: '#1e293b' }}>{formatTime(problem.timeSpent)}</div>
                          </div>
                        )}
                      </div>

                      {/* Related Concepts */}
                      {problem.relatedConcepts && problem.relatedConcepts.length > 0 && (
                        <div style={{
                          marginBottom: '24px',
                          background: '#f0f9ff',
                          borderRadius: '12px',
                          borderLeft: '4px solid #0ea5e9',
                          padding: '20px'
                        }}>
                          <h4 style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#0c4a6e',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <span style={{ fontSize: '18px' }}>🧠</span>
                            Related Concepts:
                          </h4>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px'
                          }}>
                            {problem.relatedConcepts.map((concept, index) => (
                              <span
                                key={index}
                                style={{
                                  background: '#0ea5e9',
                                  color: 'white',
                                  padding: '6px 12px',
                                  borderRadius: '20px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  textTransform: 'capitalize'
                                }}
                              >
                                {concept}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'flex-end'
                      }}>
                        {problem.aopsSourceLink && (
                          <a
                            href={problem.aopsSourceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '12px 24px',
                              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
                              color: 'white',
                              borderRadius: '8px',
                              textDecoration: 'none',
                              fontWeight: '600',
                              fontSize: '16px',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(63, 142, 246, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                              <polyline points="15 3 21 3 21 9"></polyline>
                              <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                            View Solution
                          </a>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AMCTestResults;